/**
 * Hyderabad school-decision model.
 *
 * Why this exists:
 *   Public-school zoning matters in Melbourne; it doesn't in Hyderabad.
 *   Families in HYD pick schools by name/board, not by postcode. The
 *   relevant question is: "How easy is it to reach good schools from
 *   THIS suburb?". This module computes a proximity-based summary so
 *   the UI can replace Melbourne-style "public rank zone" language with
 *   something that makes sense locally.
 *
 * Approach:
 *   - Reads HYD rows from the shared `SCHOOL_RANKS` dataset
 *     (`city === "hyd"`).
 *   - Distance = haversine between suburb coord and the school's host
 *     suburb coord (exact school coords would be finer-grained but the
 *     hosting-suburb coord is already in the verdict dataset and is
 *     good enough for "close / medium / far" framing).
 *   - Tier mapping keyed to decision clarity, not pixel-perfect distance:
 *       strong  — ≥2 top-rank schools within 5 km
 *       mixed   — 1 school within 5 km, or closest within 10 km
 *       weak    — closest top school ≥10 km
 *       unknown — no host coord (should be rare; falls through safely)
 *
 * No scoring effect — this is display-layer only. `priorityFit` and the
 * shortlist ranker never read from this module.
 */

import type { Verdict } from "../data/verdicts";
import type { SchoolRank } from "../data/school-ranks";
import { SCHOOL_RANKS } from "../data/school-ranks";

export type ProximityTier = "strong" | "mixed" | "weak" | "unknown";

export interface NearbySchool {
  school: SchoolRank;
  distanceKm: number;
}

export interface ProximitySummary {
  tier: ProximityTier;
  /** Distance to the single closest HYD top school, in km. */
  nearestKm: number | null;
  /** How many HYD top schools sit within 5 km of the suburb. */
  withinFive: number;
  /** How many HYD top schools sit within 10 km of the suburb. */
  withinTen: number;
}

/* -------------------------------------------------------------------------- */
/*  Distance                                                                  */
/* -------------------------------------------------------------------------- */

/** Great-circle distance in km between two [lng, lat] coords. */
function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const [lng1, lat1] = a;
  const [lng2, lat2] = b;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h =
    s1 * s1 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/* -------------------------------------------------------------------------- */
/*  Lookups                                                                   */
/* -------------------------------------------------------------------------- */

function getHydSchools(): SchoolRank[] {
  return SCHOOL_RANKS.filter((s) => s.city === "hyd");
}

/**
 * Top-N closest HYD schools to a given suburb, sorted by distance.
 * Returns [] when the suburb isn't found or the dataset has no HYD rows
 * with resolvable host coords.
 */
export function nearestTopSchools(
  suburbId: string,
  verdicts: readonly Verdict[],
  limit = 2
): NearbySchool[] {
  const byId = new Map<string, Verdict>();
  for (const v of verdicts) byId.set(v.id, v);
  const origin = byId.get(suburbId);
  if (!origin) return [];

  const out: NearbySchool[] = [];
  for (const s of getHydSchools()) {
    const host = byId.get(s.suburbId);
    if (!host) continue;
    const d = haversineKm(origin.coord, host.coord);
    out.push({ school: s, distanceKm: d });
  }
  out.sort((a, b) => a.distanceKm - b.distanceKm);
  return out.slice(0, limit);
}

/**
 * Full per-suburb summary used by badge/positive/popup copy. Counts
 * schools within the two thresholds + returns the closest distance.
 */
export function proximitySummary(
  suburbId: string,
  verdicts: readonly Verdict[]
): ProximitySummary {
  const byId = new Map<string, Verdict>();
  for (const v of verdicts) byId.set(v.id, v);
  const origin = byId.get(suburbId);
  if (!origin) {
    return { tier: "unknown", nearestKm: null, withinFive: 0, withinTen: 0 };
  }

  let nearest = Infinity;
  let withinFive = 0;
  let withinTen = 0;
  let any = false;
  for (const s of getHydSchools()) {
    const host = byId.get(s.suburbId);
    if (!host) continue;
    any = true;
    const d = haversineKm(origin.coord, host.coord);
    if (d < nearest) nearest = d;
    if (d <= 5) withinFive++;
    if (d <= 10) withinTen++;
  }
  if (!any) {
    return { tier: "unknown", nearestKm: null, withinFive: 0, withinTen: 0 };
  }

  const tier: ProximityTier =
    withinFive >= 2 ? "strong"
    : withinFive >= 1 || nearest <= 10 ? "mixed"
    : "weak";
  return {
    tier,
    nearestKm: nearest === Infinity ? null : nearest,
    withinFive,
    withinTen,
  };
}

/* -------------------------------------------------------------------------- */
/*  Copy helpers — the UI-ready phrases                                       */
/* -------------------------------------------------------------------------- */

/**
 * Short decision-line phrasing, used for the shortlist per-card school
 * line and for the comparator positive under HYD school priority.
 */
export function proximityLine(summary: ProximitySummary): string {
  if (summary.tier === "unknown") return "School proximity data unavailable";
  if (summary.tier === "strong") {
    return `Near several top schools — ${summary.withinFive} within 5 km`;
  }
  if (summary.tier === "mixed") {
    if (summary.withinFive >= 1) {
      return `Close to a top school — ${summary.withinFive} within 5 km`;
    }
    const km = summary.nearestKm != null ? summary.nearestKm.toFixed(1) : "—";
    return `Top schools within reach — nearest ${km} km`;
  }
  const km = summary.nearestKm != null ? summary.nearestKm.toFixed(1) : "—";
  return `Farther from top school clusters — nearest ${km} km`;
}

/**
 * Compact badge text for the comparator block's school slot.
 */
export function proximityBadge(summary: ProximitySummary): string {
  if (summary.tier === "unknown") return "School proximity: n/a";
  if (summary.withinFive >= 1) {
    return `Schools: ${summary.withinFive} within 5 km`;
  }
  if (summary.nearestKm != null) {
    return `Schools: nearest ${summary.nearestKm.toFixed(1)} km`;
  }
  return "School proximity: n/a";
}
